import {
  Button,
  FormCheckbox,
  FormInput,
  FormPassword,
  FormRoot,
  Loader,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import z from "zod";
import { authClient } from "../api/client";
import { useAuthState } from "../model/useAuthState";

const SignInFormSchema = z.object({
  email: z.string().email({ message: "ອີເມວບໍ່ຖືກຕ້ອງ" }),
  password: z.string().min(6, { message: "ລະຫັດຜ່ານຕ້ອງຢ່າງນ້ອຍ 6 ຕົວອັກສອນ" }),
  rememberMe: z.boolean().optional(),
});

type ISignInFormSchema = z.infer<typeof SignInFormSchema>;

export default function SignInForm() {
  const navigate = useNavigate({ from: "/" });
  const { isLoading } = useAuthState();

  const form = RHF.useForm({
    defaultValues: { email: "", password: "" },
    resolver: zodResolver(SignInFormSchema),
  });

  const handleSubmit = async (value: ISignInFormSchema) => {
    await authClient.signIn.email(
      { email: value.email, password: value.password },
      {
        onSuccess: () => {
          navigate({ to: "/app" });
          toast.success("ເຂົ້າລະບົບສຳເລັດ");
        },
        onError: (error) => {
          toast.error(error.error.message || error.error.statusText);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center py-8">
        <Loader />
      </div>
    );
  }

  return (
    <FormRoot<ISignInFormSchema>
      methods={form}
      onSubmit={handleSubmit}
      className="space-y-5"
    >
      <FormInput
        name="email"
        label="ອີເມວ"
        type="email"
        requiredMark
        placeholder="name@example.com"
        className="font-sans"
      />
      <FormPassword
        name="password"
        label="ລະຫັດຜ່ານ"
        requiredMark
        placeholder="••••••••"
        className="font-sans"
      />

      <div className="flex items-center justify-between gap-4">
        <FormCheckbox name="rememberMe" label="ຈຳຂ້ອຍໄວ້" />
      </div>

      <Button
        type="submit"
        isLoading={isLoading}
        className="h-11 w-full bg-primary font-heading font-semibold text-base text-primary-foreground hover:bg-primary/90"
      >
        ເຂົ້າລະບົບ
      </Button>
    </FormRoot>
  );
}
